'use client';

import {Box} from '@mui/material';
import {styled, type SxProps, type Theme} from '@mui/material/styles';
import {useMemo} from 'react';
import {sanitizeLegalHtml} from '../../shared/utils/legal-text';

export interface LegalContentProps {
  html: string;
  className?: string;
  sx?: SxProps<Theme>;
}

// Long-form legal prose: unlike RichContent (news bodies) this keeps the
// document's heading structure, never clamps, and never collapses.
const Prose = styled(Box)(({theme}) => {
  const body = {fontSize: theme.font.size['body-2'], fontWeight: theme.font.weight.regular, lineHeight: 1.2};
  const sectionHeading = {
    fontFamily: theme.font.family.primary,
    fontSize: theme.font.size['title-6'],
    fontWeight: theme.font.weight.semiBold,
    lineHeight: 1.2,
    textTransform: 'capitalize',
  };
  const subHeading = {fontSize: theme.font.size['body-1'], fontWeight: theme.font.weight.semiBold, lineHeight: 1.2};

  return {
    color: theme.palette.colors.text.neutral.default,
    ...body,
    // Long prose needs more leading than the 1.2 the token carries for UI text.
    lineHeight: 1.7,

    '& h2, & h3, & h4': {
      textTransform: 'none',
      color: theme.palette.colors.text.neutral.strong,
      marginBottom: theme.spacing(1.5),
    },
    '& h2': {...sectionHeading, textTransform: 'none', marginTop: theme.spacing(5)},
    '& h3, & h4': {...subHeading, fontFamily: theme.font.family.primary, marginTop: theme.spacing(3)},
    // :first-child, not :first-of-type — the latter also matches the first h2,
    // collapsing the gap above the opening section heading.
    '& > :first-child': {marginTop: 0},

    '& p': {margin: 0},
    '& p + p': {marginTop: theme.spacing(2)},
    '& p + ul, & p + ol, & ul + p, & ol + p': {marginTop: theme.spacing(2)},

    '& strong, & b': {fontWeight: theme.typography.fontWeightBold},
    '& em, & i': {fontStyle: 'italic'},

    '& ul, & ol': {
      margin: 0,
      paddingInlineStart: theme.spacing(3),
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
    },

    '& a': {
      color: theme.palette.colors.text.primary.default,
      textDecoration: 'underline',
      textUnderlineOffset: 2,
    },
  };
});

export function LegalContent({html, className, sx}: LegalContentProps) {
  const safe = useMemo(() => sanitizeLegalHtml(html), [html]);
  return <Prose className={className} sx={sx} dangerouslySetInnerHTML={{__html: safe}} />;
}

LegalContent.displayName = 'LegalContent';
